import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AlertTriangle, CheckCircle, HardDrive, Database, Trash2, RefreshCw, FileX, ArrowRight, Upload, FileUp, ChevronLeft, ChevronRight, Filter, Eye } from 'lucide-react';
import { BACKEND_URL } from '../lib/api';

const API_URL = BACKEND_URL;

const TIPOS_DOCUMENTO = [
  { value: 'bl', label: 'B/L (Bill of Lading)' },
  { value: 'factura_origen', label: 'Factura Origen' },
  { value: 'packing_list', label: 'Packing List' },
  { value: 'certificado_sanitario', label: 'Certificado Sanitario' },
  { value: 'certificado_captura', label: 'Certificado de Captura' },
  { value: 'certificado_exportacion', label: 'Certificado de Exportación' },
  { value: 'cso', label: 'CSO' },
  { value: 'anexo_iv', label: 'Anexo IV' },
  { value: 'dua', label: 'DUA' },
  { value: 'nota_valor', label: 'Nota de Valor' },
  { value: 'etiqueta', label: 'Etiqueta' },
  { value: 'factura_cliente', label: 'Factura Cliente' },
  { value: 'otros', label: 'Otros' },
];

const FILTROS_STORAGE = [
  { value: 'all', label: 'Todos los documentos' },
  { value: 'gridfs', label: 'En GridFS' },
  { value: 'disk', label: 'En Disco' },
  { value: 'broken', label: 'Rotos / Sin archivo' },
  { value: 'orphan', label: 'Huérfanos' },
];

const StorageManagement = () => {
  const [storageStatus, setStorageStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migratingId, setMigratingId] = useState(null);
  
  // Paginación y filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [filterType, setFilterType] = useState('all');
  const [paginacion, setPaginacion] = useState(null);
  
  // Modal de subida para reemplazar documento roto
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [batches, setBatches] = useState([]);
  const fileInputRef = useRef(null);
  
  // Modal para adoptar archivo huérfano
  const [adoptModalOpen, setAdoptModalOpen] = useState(false);
  const [selectedOrphan, setSelectedOrphan] = useState(null);
  const [adopting, setAdopting] = useState(false);
  
  // Facturas OLD
  const [oldInvoices, setOldInvoices] = useState([]);
  const [loadingOld, setLoadingOld] = useState(false);
  const [selectedOldInvoices, setSelectedOldInvoices] = useState([]);
  const [showDeleteOldDialog, setShowDeleteOldDialog] = useState(false);
  const [deletingOld, setDeletingOld] = useState(false);
  const [deleteOldMode, setDeleteOldMode] = useState('selected'); // 'selected', 'batch', 'all'
  const [selectedBatchForDelete, setSelectedBatchForDelete] = useState(null);

  const fetchStorageStatus = useCallback(async (page = currentPage, filter = filterType) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/storage-status?page=${page}&per_page=${perPage}&filter_type=${filter}`, 
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        throw new Error('Error al obtener estado del almacenamiento');
      }
      
      const data = await response.json();
      setStorageStatus(data);
      setPaginacion(data.paginacion);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, filterType]);

  const fetchBatches = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/batches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBatches(data);
      }
    } catch (err) {
      console.error('Error fetching batches:', err);
    }
  }, []);
  
  const fetchOldInvoices = useCallback(async () => {
    setLoadingOld(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices/old`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOldInvoices(data);
      }
    } catch (err) {
      console.error('Error fetching old invoices:', err);
    } finally {
      setLoadingOld(false);
    }
  }, []);

  useEffect(() => {
    fetchStorageStatus();
    fetchBatches();
    fetchOldInvoices();
  }, [fetchStorageStatus, fetchBatches, fetchOldInvoices]);

  // Funciones para manejar facturas OLD
  const handleDeleteOldInvoices = async () => {
    setDeletingOld(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (deleteOldMode === 'all') {
        endpoint = `${API_URL}/api/invoices/old/all`;
      } else if (deleteOldMode === 'batch' && selectedBatchForDelete) {
        endpoint = `${API_URL}/api/invoices/old/batch/${selectedBatchForDelete}`;
      } else {
        // Eliminar seleccionados uno por uno
        for (const invoiceId of selectedOldInvoices) {
          await fetch(`${API_URL}/api/invoices/old/${invoiceId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
        setSelectedOldInvoices([]);
        fetchOldInvoices();
        setShowDeleteOldDialog(false);
        setDeletingOld(false);
        return;
      }
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSelectedOldInvoices([]);
        fetchOldInvoices();
      }
    } catch (err) {
      console.error('Error deleting old invoices:', err);
    } finally {
      setShowDeleteOldDialog(false);
      setDeletingOld(false);
    }
  };
  
  const handleRestoreOldInvoice = async (invoiceId) => {
    if (!window.confirm('¿Restaurar esta factura como la versión activa? La factura actual será archivada.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices/old/${invoiceId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('Factura restaurada correctamente');
        fetchOldInvoices();
      } else {
        const data = await response.json();
        alert(data.detail || 'Error al restaurar');
      }
    } catch (err) {
      alert('Error al restaurar la factura');
    }
  };
  
  const toggleSelectOldInvoice = (invoiceId) => {
    setSelectedOldInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };
  
  const selectAllOldInvoices = () => {
    if (selectedOldInvoices.length === oldInvoices.length) {
      setSelectedOldInvoices([]);
    } else {
      setSelectedOldInvoices(oldInvoices.map(inv => inv.id));
    }
  };

  const handleDeleteBroken = async (docId) => {
    if (!window.confirm('¿Eliminar este documento roto? Se moverá a la papelera.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/delete-broken-document/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al eliminar');
      }
      
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    }
  };

  const handlePurgeAll = async () => {
    if (!window.confirm('¿Eliminar TODOS los documentos rotos? Esta acción moverá todos a la papelera.')) return;
    if (!window.confirm('¿Estás seguro? Esta acción no se puede deshacer fácilmente.')) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/purge-broken-documents`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al purgar');
      }
      
      alert(`Se eliminaron ${data.eliminados} documentos rotos`);
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleMigrateOne = async (docId) => {
    setMigratingId(docId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/migrate-to-gridfs/${docId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al migrar');
      }
      
      alert(`Documento migrado correctamente a GridFS`);
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setMigratingId(null);
    }
  };

  const handleMigrateAll = async () => {
    if (!window.confirm('¿Migrar TODOS los documentos del disco a GridFS? Esto protegerá los archivos de futuros Redeploys.')) return;
    
    setMigrating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/migrate-all-to-gridfs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al migrar');
      }
      
      alert(`Migración completada:\n- Migrados: ${data.migrados}\n- Errores: ${data.errores}\n- Saltados: ${data.saltados}`);
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setMigrating(false);
    }
  };

  const goToBatch = (numeroLote) => {
    if (numeroLote) {
      window.location.href = `/batches?search=${numeroLote}`;
    }
  };

  // Abrir modal de subida para documento roto
  const openUploadModal = (doc) => {
    setSelectedDoc({
      ...doc,
      tipo_documento_nuevo: doc.tipo_documento,
      numero_lote_nuevo: doc.numero_lote
    });
    setUploadFile(null);
    setUploadModalOpen(true);
  };

  // Subir archivo para reemplazar documento roto
  const handleUploadReplacement = async () => {
    if (!uploadFile || !selectedDoc) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('tipo_documento', selectedDoc.tipo_documento_nuevo);
      formData.append('numero_lote', selectedDoc.numero_lote_nuevo);
      formData.append('replace_broken_id', selectedDoc.id);
      
      const response = await fetch(`${API_URL}/api/admin/upload-replacement`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al subir archivo');
      }
      
      alert('Documento subido correctamente a GridFS');
      setUploadModalOpen(false);
      setSelectedDoc(null);
      setUploadFile(null);
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Abrir modal para adoptar archivo huérfano
  const openAdoptModal = (orphan) => {
    setSelectedOrphan({
      ...orphan,
      tipo_documento: 'otros',
      numero_lote: ''
    });
    setAdoptModalOpen(true);
  };

  // Adoptar archivo huérfano
  const handleAdoptOrphan = async () => {
    if (!selectedOrphan || !selectedOrphan.numero_lote) {
      alert('Debes seleccionar un lote');
      return;
    }
    
    setAdopting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('ruta_archivo', selectedOrphan.ruta_completa);
      formData.append('tipo_documento', selectedOrphan.tipo_documento);
      formData.append('numero_lote', selectedOrphan.numero_lote);
      
      const response = await fetch(`${API_URL}/api/admin/adopt-orphan-file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al adoptar archivo');
      }
      
      alert('Archivo adoptado y subido a GridFS correctamente');
      setAdoptModalOpen(false);
      setSelectedOrphan(null);
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setAdopting(false);
    }
  };

  // Eliminar archivo huérfano
  const handleDeleteOrphan = async (orphan) => {
    if (!window.confirm(`¿Eliminar el archivo "${orphan.nombre_archivo}"? Esta acción no se puede deshacer.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/delete-orphan-file?ruta_archivo=${encodeURIComponent(orphan.ruta_completa)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al eliminar');
      }
      
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    }
  };

  // Eliminar TODOS los archivos huérfanos
  const handlePurgeAllOrphans = async () => {
    const count = storageStatus?.archivos_huerfanos?.length || 0;
    if (!window.confirm(`¿Eliminar TODOS los ${count} archivos huérfanos? Esta acción no se puede deshacer.`)) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/purge-orphan-files`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al eliminar');
      }
      
      alert(`${data.eliminados} archivos huérfanos eliminados correctamente`);
      fetchStorageStatus();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Cargando estado del almacenamiento...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        Error: {error}
        <Button variant="outline" size="sm" className="ml-4" onClick={fetchStorageStatus}>
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="storage-management">
      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Documentos</p>
                <p className="text-2xl font-bold">{storageStatus?.resumen?.total_documentos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Funcionando</p>
                <p className="text-2xl font-bold text-green-600">{storageStatus?.resumen?.funcionando || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Rotos</p>
                <p className="text-2xl font-bold text-red-600">{storageStatus?.resumen?.rotos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <HardDrive className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">GridFS</p>
                <p className="text-2xl font-bold">{storageStatus?.gridfs_stats?.total_size_mb || 0} MB</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileX className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Huérfanos</p>
                <p className="text-2xl font-bold text-orange-600">{storageStatus?.resumen?.huerfanos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros y Paginación */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Filtrar por:</span>
              </div>
              <Select value={filterType} onValueChange={(value) => {
                setFilterType(value);
                setCurrentPage(1);
                fetchStorageStatus(1, value);
              }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Seleccionar filtro" />
                </SelectTrigger>
                <SelectContent>
                  {FILTROS_STORAGE.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {paginacion && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  Mostrando {((paginacion.pagina_actual - 1) * paginacion.por_pagina) + 1} - {Math.min(paginacion.pagina_actual * paginacion.por_pagina, paginacion.total_items)} de {paginacion.total_items}
                </span>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={paginacion.pagina_actual <= 1}
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      fetchStorageStatus(newPage, filterType);
                    }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm">
                    Página {paginacion.pagina_actual} de {paginacion.total_paginas}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={paginacion.pagina_actual >= paginacion.total_paginas}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                      fetchStorageStatus(newPage, filterType);
                    }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        
        {/* Tabla de documentos paginados */}
        {storageStatus?.documentos_paginados?.length > 0 && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Estado</th>
                    <th className="px-4 py-2 text-left">Tipo</th>
                    <th className="px-4 py-2 text-left">Nombre</th>
                    <th className="px-4 py-2 text-left">Lote</th>
                    <th className="px-4 py-2 text-left">Almacenamiento</th>
                    <th className="px-4 py-2 text-left">Tamaño</th>
                  </tr>
                </thead>
                <tbody>
                  {storageStatus.documentos_paginados.map((doc) => (
                    <tr key={doc.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {doc.estado?.includes('ok') ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" /> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Error
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 capitalize">{doc.tipo_documento?.replace(/_/g, ' ') || doc.carpeta || '-'}</td>
                      <td className="px-4 py-2">
                        <span className="font-mono text-xs max-w-[200px] truncate block" title={doc.nombre_archivo}>
                          {doc.nombre_archivo?.substring(0, 40)}{doc.nombre_archivo?.length > 40 ? '...' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{doc.numero_lote || doc.carpeta || '-'}</td>
                      <td className="px-4 py-2">
                        {doc.almacenamiento === 'GridFS' ? (
                          <span className="inline-flex items-center text-purple-600">
                            <Database className="w-3 h-3 mr-1" /> GridFS
                          </span>
                        ) : doc.almacenamiento === 'Disco' ? (
                          <span className="inline-flex items-center text-blue-600">
                            <HardDrive className="w-3 h-3 mr-1" /> Disco
                          </span>
                        ) : (
                          <span className="text-red-500">{doc.almacenamiento || 'Huérfano'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-500">{formatBytes(doc.tamano)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Documentos Rotos */}
      {storageStatus?.documentos_rotos?.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="bg-red-50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <FileX className="w-5 h-5" />
                Documentos Rotos ({storageStatus.documentos_rotos.length})
              </CardTitle>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handlePurgeAll}
                disabled={deleting}
                data-testid="purge-all-btn"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Todos
                  </>
                )}
              </Button>
            </div>
            <p className="text-sm text-red-600 mt-1">
              Estos documentos existen en la base de datos pero no tienen archivo accesible. 
              Debes eliminarlos y volver a subir los archivos.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Tipo</th>
                    <th className="px-4 py-2 text-left">Nombre</th>
                    <th className="px-4 py-2 text-left">Lote</th>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {storageStatus.documentos_rotos.map((doc) => (
                    <tr key={doc.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                          {doc.tipo_documento}
                        </span>
                      </td>
                      <td className="px-4 py-2 max-w-xs truncate" title={doc.nombre_archivo}>
                        {doc.nombre_archivo}
                      </td>
                      <td className="px-4 py-2">
                        {doc.numero_lote ? (
                          <button 
                            onClick={() => goToBatch(doc.numero_lote)}
                            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            {doc.numero_lote}
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-2 text-gray-500">{formatDate(doc.created_at)}</td>
                      <td className="px-4 py-2 text-center space-x-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                          onClick={() => openUploadModal(doc)}
                          title="Subir archivo para este documento"
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Subir
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteBroken(doc.id)}
                          data-testid={`delete-broken-${doc.id}`}
                          title="Eliminar registro"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documentos en Disco (Legacy) */}
      {storageStatus?.documentos_disco?.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader className="bg-yellow-50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-yellow-700">
                <HardDrive className="w-5 h-5" />
                Documentos en Disco ({storageStatus?.resumen?.en_disco || 0})
              </CardTitle>
              <Button 
                variant="default" 
                size="sm"
                onClick={handleMigrateAll}
                disabled={migrating}
                className="bg-yellow-600 hover:bg-yellow-700"
                data-testid="migrate-all-btn"
              >
                {migrating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Migrando...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Migrar Todos a GridFS
                  </>
                )}
              </Button>
            </div>
            <p className="text-sm text-yellow-600 mt-1">
              ⚠️ Estos documentos están en riesgo de perderse. Migrarlos a GridFS los protegerá permanentemente.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Tipo</th>
                    <th className="px-4 py-2 text-left">Nombre</th>
                    <th className="px-4 py-2 text-left">Lote</th>
                    <th className="px-4 py-2 text-left">Tamaño</th>
                    <th className="px-4 py-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {storageStatus.documentos_disco.map((doc) => (
                    <tr key={doc.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                          {doc.tipo_documento}
                        </span>
                      </td>
                      <td className="px-4 py-2 max-w-xs truncate">{doc.nombre_archivo}</td>
                      <td className="px-4 py-2">
                        {doc.numero_lote ? (
                          <button 
                            onClick={() => goToBatch(doc.numero_lote)}
                            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            {doc.numero_lote}
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-2 text-gray-500">{formatBytes(doc.tamano)}</td>
                      <td className="px-4 py-2 text-center">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-yellow-600 hover:text-yellow-700"
                          onClick={() => handleMigrateOne(doc.id)}
                          disabled={migratingId === doc.id}
                          data-testid={`migrate-${doc.id}`}
                        >
                          {migratingId === doc.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Database className="w-4 h-4 mr-1" />
                              Migrar
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Archivos Huérfanos (en disco sin registro) */}
      {storageStatus?.archivos_huerfanos?.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader className="bg-orange-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <FileX className="w-5 h-5" />
                  Archivos Huérfanos ({storageStatus.archivos_huerfanos.length})
                </CardTitle>
                <p className="text-sm text-orange-600 mt-1">
                  Estos archivos existen en el servidor pero no tienen registro en la base de datos. 
                  Puedes asignarlos a un lote para recuperarlos.
                </p>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handlePurgeAllOrphans}
                disabled={deleting}
                className="shrink-0"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {deleting ? 'Eliminando...' : 'Eliminar Todos'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Archivo</th>
                    <th className="px-4 py-2 text-left">Ruta en Servidor</th>
                    <th className="px-4 py-2 text-left">Tamaño</th>
                    <th className="px-4 py-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {storageStatus.archivos_huerfanos.map((orphan) => (
                    <tr key={orphan.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className="font-mono text-xs">{orphan.nombre_archivo}</span>
                      </td>
                      <td className="px-4 py-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded block max-w-[350px] overflow-x-auto">
                          {orphan.ruta_completa}
                        </code>
                      </td>
                      <td className="px-4 py-2 text-gray-500">{formatBytes(orphan.tamano)}</td>
                      <td className="px-4 py-2 text-center space-x-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            window.open(`${API_URL}/api/admin/preview-orphan?ruta_archivo=${encodeURIComponent(orphan.ruta_completa)}&token=${token}`, '_blank');
                          }}
                          title="Ver contenido del archivo"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Ver
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-green-600 hover:text-green-700"
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            window.location.href = `${API_URL}/api/admin/download-orphan?ruta_archivo=${encodeURIComponent(orphan.ruta_completa)}&token=${token}`;
                          }}
                          title="Descargar archivo"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Descargar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() => openAdoptModal(orphan)}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Asignar
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteOrphan(orphan)}
                          title="Eliminar archivo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Botón de recargar */}
      <div className="flex justify-center">
        <Button variant="outline" onClick={fetchStorageStatus}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar Estado
        </Button>
      </div>

      {/* Modal de Subida */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-blue-600" />
              Subir Archivo para Documento Roto
            </DialogTitle>
          </DialogHeader>
          
          {selectedDoc && (
            <div className="space-y-4">
              {/* Info del documento actual */}
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><span className="font-medium">Documento original:</span> {selectedDoc.nombre_archivo}</p>
                <p className="text-gray-500 mt-1">Este documento no tiene archivo. Sube uno nuevo para restaurarlo.</p>
              </div>
              
              {/* Tipo de documento */}
              <div className="space-y-2">
                <Label>Tipo de Documento</Label>
                <Select 
                  value={selectedDoc.tipo_documento_nuevo} 
                  onValueChange={(value) => setSelectedDoc({...selectedDoc, tipo_documento_nuevo: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOCUMENTO.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Lote */}
              <div className="space-y-2">
                <Label>Lote</Label>
                <Select 
                  value={selectedDoc.numero_lote_nuevo || ''} 
                  onValueChange={(value) => setSelectedDoc({...selectedDoc, numero_lote_nuevo: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {batches.map(batch => (
                      <SelectItem key={batch.id} value={batch.numero_lote}>
                        {batch.numero_lote} - {batch.contenedor || 'Sin contenedor'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Selector de archivo */}
              <div className="space-y-2">
                <Label>Archivo</Label>
                <div className="flex gap-2">
                  <Input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    accept=".pdf,.PDF,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    className="flex-1"
                  />
                </div>
                {uploadFile && (
                  <p className="text-sm text-green-600">
                    ✓ Archivo seleccionado: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleUploadReplacement}
              disabled={!uploadFile || uploading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Subir a GridFS
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para Adoptar Archivo Huérfano */}
      <Dialog open={adoptModalOpen} onOpenChange={setAdoptModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-orange-600" />
              Asignar Archivo a un Lote
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrphan && (
            <div className="space-y-4">
              {/* Info del archivo */}
              <div className="bg-orange-50 p-3 rounded-lg text-sm">
                <p><span className="font-medium">Archivo:</span> {selectedOrphan.nombre_archivo}</p>
                <p><span className="font-medium">Ubicación:</span> {selectedOrphan.carpeta}</p>
                <p><span className="font-medium">Tamaño:</span> {formatBytes(selectedOrphan.tamano)}</p>
              </div>
              
              {/* Tipo de documento */}
              <div className="space-y-2">
                <Label>Tipo de Documento</Label>
                <Select 
                  value={selectedOrphan.tipo_documento} 
                  onValueChange={(value) => setSelectedOrphan({...selectedOrphan, tipo_documento: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOCUMENTO.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Lote */}
              <div className="space-y-2">
                <Label>Asignar al Lote</Label>
                <Select 
                  value={selectedOrphan.numero_lote || ''} 
                  onValueChange={(value) => setSelectedOrphan({...selectedOrphan, numero_lote: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {batches.map(batch => (
                      <SelectItem key={batch.id} value={batch.numero_lote}>
                        {batch.numero_lote} - {batch.contenedor || 'Sin contenedor'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <p className="text-sm text-gray-500">
                Al confirmar, el archivo se subirá a GridFS y quedará asociado al lote seleccionado.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdoptModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAdoptOrphan}
              disabled={!selectedOrphan?.numero_lote || adopting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {adopting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Guardar en GridFS
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Sección de Facturas OLD (Historial) */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Historial de Facturas (Versiones OLD)
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchOldInvoices}
                disabled={loadingOld}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingOld ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              {selectedOldInvoices.length > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => {
                    setDeleteOldMode('selected');
                    setShowDeleteOldDialog(true);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar ({selectedOldInvoices.length})
                </Button>
              )}
              {oldInvoices.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => {
                    setDeleteOldMode('all');
                    setShowDeleteOldDialog(true);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Todas
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingOld ? (
            <div className="text-center py-4">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
              <p className="mt-2 text-gray-500">Cargando historial...</p>
            </div>
          ) : oldInvoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay facturas en el historial
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input 
                    type="checkbox" 
                    checked={selectedOldInvoices.length === oldInvoices.length}
                    onChange={selectAllOldInvoices}
                    className="rounded"
                  />
                  Seleccionar todas
                </label>
                <span className="text-sm text-gray-500">
                  Total: {oldInvoices.length} facturas antiguas
                </span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left w-10">
                        <span className="sr-only">Seleccionar</span>
                      </th>
                      <th className="p-2 text-left">Nombre Archivo</th>
                      <th className="p-2 text-left">Tipo</th>
                      <th className="p-2 text-left">Lote</th>
                      <th className="p-2 text-left">Contenedor</th>
                      <th className="p-2 text-left">Fecha Factura</th>
                      <th className="p-2 text-left">Archivada</th>
                      <th className="p-2 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldInvoices.map(inv => (
                      <tr key={inv.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          <input 
                            type="checkbox"
                            checked={selectedOldInvoices.includes(inv.id)}
                            onChange={() => toggleSelectOldInvoice(inv.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="p-2 font-medium">{inv.nombre_archivo}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            inv.tipo === 'origen' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {inv.tipo === 'origen' ? 'Origen' : 'Cliente'}
                          </span>
                        </td>
                        <td className="p-2">{inv.numero_lote || inv.lote_numero || '-'}</td>
                        <td className="p-2">{inv.contenedor || '-'}</td>
                        <td className="p-2">{inv.fecha_factura}</td>
                        <td className="p-2 text-gray-500">{formatDate(inv.modified_at)}</td>
                        <td className="p-2">
                          <div className="flex justify-center gap-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              title="Ver factura"
                              onClick={() => {
                                // Navegar a la vista de la factura OLD
                                window.open(`/invoice-old-view/${inv.id}`, '_blank');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              title="Restaurar como activa"
                              onClick={() => handleRestoreOldInvoice(inv.id)}
                            >
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600"
                              title="Eliminar"
                              onClick={() => {
                                setSelectedOldInvoices([inv.id]);
                                setDeleteOldMode('selected');
                                setShowDeleteOldDialog(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Diálogo de confirmación para eliminar facturas OLD */}
      <Dialog open={showDeleteOldDialog} onOpenChange={setShowDeleteOldDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Eliminar Facturas del Historial
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {deleteOldMode === 'all' ? (
              <p className="text-gray-700">
                ¿Estás seguro de que deseas eliminar <strong>TODAS</strong> las {oldInvoices.length} facturas del historial?
                <br /><br />
                <span className="text-red-600 font-medium">Esta acción no se puede deshacer.</span>
              </p>
            ) : (
              <p className="text-gray-700">
                ¿Estás seguro de que deseas eliminar {selectedOldInvoices.length} factura(s) del historial?
                <br /><br />
                <span className="text-red-600 font-medium">Esta acción no se puede deshacer.</span>
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteOldDialog(false)} disabled={deletingOld}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteOldInvoices}
              disabled={deletingOld}
            >
              {deletingOld ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorageManagement;
